import React, { useEffect, useState } from 'react';
import { useOffline } from '@/contexts/OfflineContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { WifiOff, RefreshCw, Wifi } from 'lucide-react';

/**
 * OfflineIndicator — calm, wellness-toned offline awareness layer.
 *
 * Design intent (2026-05-23 launch-readiness polish):
 *   - Subtle, non-alarming, mobile-friendly.
 *   - No animate-bounce / no animate-pulse / no red gradients — the previous
 *     iteration used UI states that read as "error" or "danger" rather than
 *     "your phone is on the tube". Replaced with soft slate/amber/emerald.
 *   - Three visible states (offline / syncing / just-came-back-online), each
 *     with a single line of plain-language copy. No technical terms like
 *     "queue", "retry", "conflict".
 *   - The "back online" confirmation auto-dismisses after 2s so it never
 *     lingers as visual clutter once the user has acknowledged it.
 *   - Localized via the existing t() helper — keys exist with English
 *     fallback per the i18n contract.
 */
const OfflineIndicator = () => {
  const { isOnline, isSyncing, queueSize } = useOffline();
  const { t } = useLanguage();
  const [showBackOnline, setShowBackOnline] = useState(false);
  const [wasOffline, setWasOffline] = useState(!navigator.onLine);

  // Detect the offline → online transition so we can briefly show
  // "Back online — updates synced" without taking over the page.
  useEffect(() => {
    if (!isOnline) {
      setWasOffline(true);
      setShowBackOnline(false);
      return;
    }
    if (isOnline && wasOffline && !isSyncing) {
      setShowBackOnline(true);
      const t = setTimeout(() => {
        setShowBackOnline(false);
        setWasOffline(false);
      }, 2200);
      return () => clearTimeout(t);
    }
  }, [isOnline, isSyncing, wasOffline]);

  // Nothing to surface when fully online, idle, and no offline transition pending.
  if (isOnline && !isSyncing && !showBackOnline) return null;

  // OFFLINE — slim slate banner, no shouty colours.
  if (!isOnline) {
    return (
      <div
        className="fixed top-0 left-0 right-0 z-50 bg-slate-800/95 text-white py-2 px-4 shadow-sm backdrop-blur-sm"
        role="status"
        aria-live="polite"
        data-testid="offline-indicator-offline"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 text-xs sm:text-sm">
          <div className="flex items-center gap-2 min-w-0">
            <WifiOff className="h-4 w-4 shrink-0 text-amber-300" aria-hidden="true" />
            <span className="font-medium truncate">
              {t('offline.banner', "You're offline. Some features may be limited.")}
            </span>
          </div>
          {queueSize > 0 && (
            <span
              className="text-[11px] text-slate-300 whitespace-nowrap"
              data-testid="offline-indicator-queue-count"
            >
              {t('offline.savedLocally', 'Saved offline — syncing when online')}
            </span>
          )}
        </div>
      </div>
    );
  }

  // SYNCING — subtle emerald strip, no spinner shouting.
  if (isSyncing) {
    return (
      <div
        className="fixed top-0 left-0 right-0 z-50 bg-emerald-700/95 text-white py-1.5 px-4 shadow-sm backdrop-blur-sm"
        role="status"
        aria-live="polite"
        data-testid="offline-indicator-syncing"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-xs sm:text-sm">
          <RefreshCw className="h-3.5 w-3.5 motion-safe:animate-spin shrink-0" aria-hidden="true" />
          <span className="font-medium">
            {t('offline.syncing', 'Syncing your updates…')}
          </span>
        </div>
      </div>
    );
  }

  // BACK ONLINE — brief confirmation, auto-dismisses.
  return (
    <div
      className="fixed top-0 left-0 right-0 z-50 bg-emerald-600/95 text-white py-1.5 px-4 shadow-sm backdrop-blur-sm motion-safe:animate-in motion-safe:slide-in-from-top motion-safe:duration-200"
      role="status"
      aria-live="polite"
      data-testid="offline-indicator-back-online"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-xs sm:text-sm">
        <Wifi className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span className="font-medium">
          {t('offline.backOnline', 'Back online — updates synced')}
        </span>
      </div>
    </div>
  );
};

export default OfflineIndicator;
